import type { ReactNode } from "react";
import { Sparkline } from "@/components/charts/Sparkline";
import { cn } from "@/lib/cn";
import { count } from "@/lib/format";

/**
 * The summary tile. One number, what it counts, and — when there is a
 * series worth showing — a sparkline pinned to the bottom edge.
 *
 * The delta line says "+4 this week" rather than "+12%". A percentage on a
 * product with six users is theatre: 1 → 2 is +100%, and the honest version
 * is the two integers.
 */
export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  series,
  footer,
  onClick,
  tone = "plain",
  className,
}: {
  label: string;
  value: number | string;
  /** Absolute change over the window `deltaLabel` names. */
  delta?: number;
  deltaLabel?: string;
  series?: number[];
  footer?: ReactNode;
  onClick?: () => void;
  tone?: "plain" | "green" | "pink";
  className?: string;
}) {
  const branded = tone !== "plain";
  const body = (
    <>
      <div
        aria-hidden="true"
        className={cn(
          "absolute -right-7 -top-9 h-24 w-24 rounded-full border-[18px] opacity-75",
          tone === "green" && "border-white/55",
          tone === "pink" && "border-brand-green/45",
          tone === "plain" && "border-accent-soft",
        )}
      />
      <p className={cn("eyebrow relative", branded && "text-on-brand/65")}>
        {label}
      </p>
      <p className="relative mt-2 font-mono text-[30px] font-semibold leading-none tracking-[-0.035em] text-ink">
        {typeof value === "number" ? count(value) : value}
      </p>
      {delta !== undefined && deltaLabel ? (
        <p className="relative mt-2 text-micro">
          <span
            className={cn(
              "font-mono",
              delta > 0
                ? branded
                  ? "text-on-brand"
                  : "text-accent"
                : branded
                  ? "text-on-brand/55"
                  : "text-muted-2",
            )}
          >
            {delta > 0 ? "+" : ""}
            {count(delta)}
          </span>{" "}
          <span className={branded ? "text-on-brand/65" : "text-muted"}>
            {deltaLabel}
          </span>
        </p>
      ) : null}
      {footer ? (
        <div
          className={cn(
            "relative mt-2 text-micro",
            branded ? "text-on-brand/65" : "text-muted",
          )}
        >
          {footer}
        </div>
      ) : null}
      {series && series.length > 1 ? (
        <div className="relative mt-2 -mb-1">
          <Sparkline values={series} width={132} height={26} />
        </div>
      ) : null}
    </>
  );

  const shell = cn(
    "relative min-h-[126px] overflow-hidden rounded-panel border p-4 text-left shadow-panel",
    tone === "green" && "border-brand-green bg-brand-green",
    tone === "pink" && "border-brand-pink bg-brand-pink",
    tone === "plain" && "border-white bg-surface",
    onClick && "transition-colors hover:border-muted-2 cursor-pointer",
    className,
  );

  return onClick ? (
    <button type="button" onClick={onClick} className={shell}>
      {body}
    </button>
  ) : (
    <div className={shell}>{body}</div>
  );
}
